// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { TreeItems } from 'dnd-kit-sortable-tree';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseCheckbox, BaseInput, BaseInputSearch, BaseLabel, Loading } from '~/components/common';
// Others
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_MAX_PAGE,
  EMPTY_STRING,
  REG_EXP,
} from '~/utils/constants/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { IGroupDay } from '~/utils/interface/inventory';
import { ITreeItem } from '~/utils/interface/common';
import { formatDate, handleDaysBetween } from '~/utils/helper/common';
import { IFormOrder } from '~/utils/interface/order';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IProductGroup } from '~/utils/interface/quotes';
import { getListVariant } from '~/thunks/variant/variantThunk';
import { DateFormatEnum, TypeInventoryGroupDay } from '~/utils/enum/common';
import { defaultValuesListFormVariant, schema } from './helper';
import { IListFormVariant, IQueryParamVariant, IVariantItem } from '~/utils/interface/variant';
// Styles, images, icons
import styles from './VariantListOrderModal.module.scss';
import { icons } from '~/assets';

type Props = {
  parentId?: string;
  productId?: string;
  inventoryId?: string;
  idRemember?: string;
  idVariants?: string[];
  detailsData?: TreeItems<ITreeItem<IProductGroup>>;
  onSave?: (data: IListFormVariant, idRemember: string) => void;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const VariantListOrderModal = (props: Props) => {
  //#region Destructuring Props
  const { parentId, productId, idRemember, idVariants, inventoryId, detailsData, onClose, onSave } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const dispatch = useAppDispatch();

  const { control, handleSubmit, setValue, watch, reset } = useForm<IListFormVariant>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesListFormVariant,
  });
  const { watch: watchOrder } = useFormContext<IFormOrder>();

  const { listVariant } = watch();
  const { startDate, endDate, orderItems, orderAddOns } = watchOrder();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IQueryParamVariant>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_MAX_PAGE,
    startDate,
    endDate,
    ...(parentId ? { parentId } : {}),
    ...(productId ? { productId } : {}),
  });
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListVariant(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_MAX_PAGE,
      ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListVariant = (params: IQueryParamVariant) => {
    setIsLoading(true);

    dispatch(getListVariant(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;

        if (!responses) return;

        const dataDetailConvert = detailsData?.map((item) => item?.data);

        const dataForm: IListFormVariant = {
          listVariant: responses.map((item) => {
            const quantityDetail = dataDetailConvert?.find(
              (itemDetail) => itemDetail?.inventoryId === item?.inventoryId && itemDetail?.variantId === item.id
            )?.quantity;

            const quantityInventory =
              item?.groupDayOrder && item?.groupDayOrder?.length > 0
                ? String(Math.min(...item?.groupDayOrder?.map((order) => Number(order?.availableInventory))))
                : item?.quantity;

            return {
              ...item,
              quantityInventory: quantityInventory,
              quantity: String(dataDetailConvert && idRemember && quantityDetail ? quantityDetail : item.quantity),
              ...(idRemember && { quantityRemember: String(quantityDetail ?? 0) }),
              isChecked: idVariants?.includes(item.id) || false,
            };
          }),
        };

        reset({ listVariant: convertDataVariantGroupDate(dataForm?.listVariant || []) });
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const convertDataVariantGroupDate = (variantItems: IVariantItem[]) => {
    const sortDated = handleDaysBetween(startDate || EMPTY_STRING, endDate || EMPTY_STRING);

    return variantItems.map((item) => {
      const convertQuotes = item?.groupDayQuote?.map((quote) => ({
        ...quote,
        type: TypeInventoryGroupDay.QUOTE,
        availableInventory: quote?.availableInventory ?? '0',
      }));

      const convertOrders = item?.groupDayOrder?.map((order) => ({
        ...order,
        type: TypeInventoryGroupDay.ORDER,
        availableInventory: order?.availableInventory ?? '0',
      }));

      let mergedDays: IGroupDay[] = [];

      sortDated.forEach((date) => {
        const quote = convertQuotes?.find((quote) => quote?.day === date);
        const order = convertOrders?.find((order) => order?.day === date);

        if (quote && order) {
          mergedDays.push({
            ...order,
            type: TypeInventoryGroupDay.ALL,
          });
        } else if (order) {
          mergedDays.push(order);
        } else if (quote) {
          mergedDays.push(quote);
        } else {
          mergedDays.push({
            day: date,
            quantity: 0,
            availableInventory: '0',
          });
        }
      });

      return { ...item, mergedDays };
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleSave = (data: IListFormVariant) => {
    onSave?.(data, idRemember || EMPTY_STRING);
  };

  const handleSumQuantity = (inventoryId: string, variantId: string) => {
    return [...(orderItems ?? []), ...(orderAddOns ?? [])]
      .filter((item) => item?.inventoryId === inventoryId && item?.variantId === variantId)
      .reduce((sum, item) => sum + Number(item.quantity), 0);
  };
  //#endregion Handle Function

  return (
    <Dialog id='variantListModalComponent' open={true} onClose={onClose} fullWidth maxWidth={'lg'}>
      <DialogTitle className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel label={t('form_orders_title_variant_availability')} styleType='bigTitle' />
          </Stack>
        </Stack>

        <IconButton onClick={onClose} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Stack direction={'row'} justifyContent={'end'}>
          <BaseInputSearch
            width={300}
            typeStyle='outlined'
            onChange={handleSearchChange}
            borderRadius={100}
            placeholder={t('common_text_search', { ns: 'common/base' })}
          />
        </Stack>

        <TableContainer component={Paper} className={cx('tableContainer')}>
          <Table stickyHeader>
            <TableHead className={cx('tableHead')}>
              <TableCell width={'25%'} className={cx('titleHead')}>
                {t('form_orders_placeholder_name')}
              </TableCell>

              <TableCell width={'25%'} className={cx('titleHead')}>
                {t('form_groups_title_quantity')}
              </TableCell>

              {startDate &&
                endDate &&
                listVariant &&
                listVariant[0]?.mergedDays?.map((day, dayIndex) => (
                  <TableCell align='center' key={dayIndex} className={cx('titleHead')}>
                    {formatDate(day.day, DateFormatEnum.MMM_D)}
                  </TableCell>
                ))}
            </TableHead>

            <TableBody>
              {listVariant &&
                listVariant?.length > 0 &&
                listVariant?.map((item, itemIndex) => {
                  const fieldName = `listVariant.${itemIndex}` as const;
                  const isValid = item?.mergedDays?.every(
                    (day) =>
                      (idRemember
                        ? Number(handleSumQuantity(inventoryId!, item?.id)) -
                          Number(item?.quantityRemember) +
                          Number(item?.quantity)
                        : Number(handleSumQuantity(inventoryId!, item?.id)) + Number(item.quantity)) <=
                      (Number(day.availableInventory) !== 0
                        ? Number(day.availableInventory)
                        : Number(item.quantityInventory))
                  );

                  let rowClass = 'rowDefault';
                  let cellClass = 'cellDefault';

                  if (startDate && endDate) {
                    rowClass = Number(item.quantity) === 0 ? 'rowDefault' : isValid ? 'rowValid' : 'rowInvalid';
                    cellClass = Number(item.quantity) === 0 ? 'cellDefault' : isValid ? 'cellValid' : 'cellInvalid';
                  }

                  return (
                    <TableRow key={itemIndex} className={cx(rowClass)}>
                      <TableCell width={'25%'} className={cx('textCell', cellClass)}>
                        <Stack direction={'row'} gap={2}>
                          <Controller
                            name={`${fieldName}.isChecked`}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                              <BaseCheckbox checked={value} onChange={(value) => onChange(value)} />
                            )}
                          />
                          <Box>{item.name}</Box>
                        </Stack>
                      </TableCell>

                      <TableCell width={'25%'} align='center' className={cx('textCell', cellClass)}>
                        <Controller
                          name={`${fieldName}.quantity`}
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <Stack
                              direction='row'
                              spacing={1}
                              alignItems='center'
                              justifyItems={'center'}
                              gap={2}
                              className={cx('viewQuantity')}
                            >
                              <IconButton
                                className={cx('buttonQty')}
                                onClick={() => {
                                  const newQty = Math.max(Number(value) - 1, 0);
                                  setValue(`${fieldName}.quantity`, newQty.toString());
                                }}
                              >
                                <img src={icons.commonIconMinus} alt={t('common_alt_img')} />
                              </IconButton>

                              <BaseInput
                                height={31}
                                textAlignCenter
                                width={55}
                                value={value}
                                borderRadius={'4px'}
                                onChange={(e) => {
                                  const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                                  const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                                  onChange(numericValue);
                                }}
                              />

                              <IconButton
                                className={cx('buttonQty')}
                                onClick={() => {
                                  const newQty = Number(value) + 1;
                                  setValue(`${fieldName}.quantity`, newQty.toString());
                                }}
                              >
                                <img src={icons.commonIconPlusGrey} alt={t('common_alt_img')} />
                              </IconButton>
                            </Stack>
                          )}
                        />
                      </TableCell>

                      {startDate &&
                        endDate &&
                        item.mergedDays?.map((day, dayIndex) => (
                          <TableCell
                            align='center'
                            className={cx(
                              'textCell',
                              cellClass,
                              (idRemember
                                ? Number(handleSumQuantity(inventoryId!, item?.id)) -
                                  Number(item?.quantityRemember) +
                                  Number(item?.quantity)
                                : Number(handleSumQuantity(inventoryId!, item?.id)) + Number(item.quantity)) <=
                                (Number(day.availableInventory) !== 0
                                  ? Number(day.availableInventory)
                                  : Number(item.quantityInventory))
                                ? 'textValid'
                                : 'textInvalid'
                            )}
                            key={dayIndex}
                          >
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} gap={1}>
                              {(() => {
                                const available = Number(day.availableInventory);
                                const quantityInventory = Number(item.quantityInventory);
                                const quantity = idRemember
                                  ? Number(handleSumQuantity(inventoryId!, item?.id)) -
                                    Number(item?.quantityRemember) +
                                    Number(item?.quantity)
                                  : Number(handleSumQuantity(inventoryId!, item?.id)) + Number(item.quantity);

                                if (!available && !quantityInventory) {
                                  return `${quantity}/0`;
                                }
                                return `${quantity}/${available !== 0 ? available : quantityInventory}`;
                              })()}

                              {day.type ? (
                                day.type === TypeInventoryGroupDay.ALL ? (
                                  <Stack direction={'row'}>
                                    <IconButton className={cx('buttonDay')}>
                                      <Box className={cx('viewBoxType')}>
                                        <Typography className={cx('textType')}>{'Q'}</Typography>
                                      </Box>
                                    </IconButton>

                                    <IconButton className={cx('buttonDay')}>
                                      <Box className={cx('viewBoxType')}>
                                        <Typography className={cx('textType')}>{'O'}</Typography>
                                      </Box>
                                    </IconButton>
                                  </Stack>
                                ) : (
                                  <>
                                    <IconButton className={cx('buttonDay')}>
                                      <Box className={cx('viewBoxType')}>
                                        <Typography className={cx('textType')}>
                                          {day.type === TypeInventoryGroupDay.ORDER ? 'O' : 'Q'}
                                        </Typography>
                                      </Box>
                                    </IconButton>
                                  </>
                                )
                              ) : (
                                <Box className={cx('viewBoxNo')} />
                              )}
                            </Stack>
                          </TableCell>
                        ))}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={onClose}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button variant='contained' className={cx('buttonSave')} onClick={handleSubmit(handleSave)}>
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default VariantListOrderModal;
